/**
 * 다국어 지원 설정
 *
 * 백엔드 API와 동기화되는 로케일 설정입니다.
 * @see olulo-mx-admin/docs/specs/SPEC-192-i18n-api.md
 */

/** 지원 언어 코드 (15개국어 — config/translatable.php와 동기화) */
export type Locale =
  | 'es' // Español (기본값)
  | 'en' // English
  | 'ko' // 한국어
  | 'pt' // Português
  | 'fr' // Français
  | 'zh' // 中文
  | 'ja' // 日本語
  | 'hi' // हिन्दी
  | 'de' // Deutsch
  | 'it' // Italiano
  | 'ru' // Русский
  | 'ar' // العربية (Arabic)
  | 'vi' // Tiếng Việt (Vietnamese)
  | 'th' // ไทย (Thai)
  | 'nl'; // Nederlands (Dutch)

/** 언어 정보 */
export type LocaleDirection = 'ltr' | 'rtl';

export interface LocaleInfo {
  code: Locale;
  name: string;
  nativeName: string;
  languageIcon: string;
  tier: 1 | 2 | 3 | 4 | 5;
  direction: LocaleDirection;
}

/** 지원 언어 목록 (티어별 정렬 — config/translatable.php와 동기화) */
export const SUPPORTED_LOCALES: LocaleInfo[] = [
  // Tier 1: 핵심 언어 (필수)
  { code: 'es', name: 'Spanish', nativeName: 'Español', languageIcon: 'Ñ', tier: 1, direction: 'ltr' },
  { code: 'en', name: 'English', nativeName: 'English', languageIcon: 'En', tier: 1, direction: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', languageIcon: '가', tier: 1, direction: 'ltr' },
  // Tier 2: 아메리카 언어
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', languageIcon: 'Ã', tier: 2, direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', languageIcon: 'Ç', tier: 2, direction: 'ltr' },
  // Tier 3: 아시아 언어
  { code: 'zh', name: 'Chinese', nativeName: '中文', languageIcon: '文', tier: 3, direction: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', languageIcon: 'あ', tier: 3, direction: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', languageIcon: 'अ', tier: 3, direction: 'ltr' },
  // Tier 4: 유럽 언어
  { code: 'de', name: 'German', nativeName: 'Deutsch', languageIcon: 'ß', tier: 4, direction: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', languageIcon: 'It', tier: 4, direction: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', languageIcon: 'Я', tier: 4, direction: 'ltr' },
  // Tier 5: 글로벌 언어
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', languageIcon: 'ع', tier: 5, direction: 'rtl' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', languageIcon: 'ă', tier: 5, direction: 'ltr' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', languageIcon: 'ก', tier: 5, direction: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', languageIcon: 'Ĳ', tier: 5, direction: 'ltr' },
];

/** 기본 로케일 */
export const DEFAULT_LOCALE: Locale = 'es';

/** 브라우저 언어가 지원되지 않을 때의 fallback 로케일 */
export const BROWSER_LOCALE_FALLBACK: Locale = 'en';

/** 로케일 코드 목록 */
export const LOCALE_CODES = SUPPORTED_LOCALES.map((l) => l.code);

/** 로케일 코드 유효성 검사 */
export function isValidLocale(code: string): code is Locale {
  return LOCALE_CODES.includes(code as Locale);
}

/**
 * 멕시코 서비스 도메인 목록
 *
 * 서비스 메뉴 도메인 목록.
 * 과거 Stage 2 에서 첫 방문 es 강제에 사용했으나, #44 재오픈 이후 브라우저 언어 감지를 우선한다.
 *
 * @see Stage 2 (#387) — 도메인 기반 default locale 강제 (옵션 A)
 * @see #44 — QR 첫 진입 navigator.language 자동 감지
 */
export const MEXICO_DOMAINS = [
  'menu.olulo.com.mx',
  'menu.dev.olulo.com.mx',
  'menu.staging.olulo.com.mx',
];

/**
 * BCP-47 언어 태그에서 매칭 후보 리스트 생성 (구체적 → 일반 순서 보장)
 *
 * #599 codex R2: primary 로 바로 축약하지 않고 중간 variant 단계를 후보로 보존한다.
 * 향후 LOCALE_CODES 에 'zh-tw' 같은 region variant 가 추가되면 primary 보다 우선 매칭된다.
 *
 * 생성 규칙 (subtag 를 끝에서 하나씩 제거 + region 우선 보존 변형 포함):
 * - `zh-hant-tw` → ['zh-hant-tw', 'zh-tw', 'zh-hant', 'zh']
 * - `en-us` → ['en-us', 'en']
 * - `en-us-x-private` → private extension(`-x-` 이후) 제거 후 ['en-us', 'en']
 * - `en-us-u-ca-buddhist` → Unicode extension(`-u-` 이후) 제거 후 ['en-us', 'en']
 */
function buildLanguageTagCandidates(normalized: string): string[] {
  // BCP-47 singleton extension 제거 — `-x-`(private use) 뿐 아니라 `-u-`(Unicode),
  // `-t-`(transformed) 등 1글자 singleton 이후 subtag 전부 후보 생성에서 제외
  const base = normalized.replace(/-[a-z0-9]-.*$/, '');
  const subtags = base.split('-').filter(Boolean);
  if (subtags.length === 0) {
    return [];
  }

  const candidates: string[] = [];
  const push = (candidate: string): void => {
    if (!candidates.includes(candidate)) {
      candidates.push(candidate);
    }
  };

  // 끝 subtag 부터 하나씩 제거하며 후보 생성. 각 단계에서 region 우선 보존 변형
  // (primary + 현재 끝 subtag, 예: zh-hant-tw 단계의 zh-tw) 을 truncation 보다 먼저 시도.
  for (let length = subtags.length; length >= 2; length--) {
    push(subtags.slice(0, length).join('-'));
    if (length > 2) {
      push(`${subtags[0]}-${subtags[length - 1]}`);
    }
  }

  // primary 단독 fallback 은 후보 순회 마지막
  push(subtags[0]);

  return candidates;
}

/**
 * BCP-47 언어 태그 1개를 지원 로케일로 정규화
 *
 * #599: iOS Safari 는 `ko-KR`, `zh-Hans-CN`, `pt-BR`, `es-419` 등
 * 지역/스크립트 서브태그 포함 형식을 반환하므로 후보 리스트를 순서대로 매칭한다.
 *
 * 매칭: buildLanguageTagCandidates 가 생성한 순서 보장 후보(구체적 → 일반)를
 * LOCALE_CODES 와 exact 비교 (대소문자 무시 — BCP-47 casing vs 소문자 코드).
 *
 * @returns 매칭된 로케일, 실패 시 null
 */
function normalizeLanguageTag(tag: string): Locale | null {
  const normalized = tag.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  for (const candidate of buildLanguageTagCandidates(normalized)) {
    const matched = LOCALE_CODES.find((code) => code.toLowerCase() === candidate);
    if (matched) {
      return matched;
    }
  }

  return null;
}

/**
 * 브라우저 언어에서 로케일 감지
 *
 * #599: navigator.language 단일 값만 보지 않고 navigator.languages 후보 전체를
 * 우선순위대로 순회한다. 첫 후보가 미지원 언어(예: sw-KE)여도 다음 후보(예: ko-KR)가
 * 지원 언어면 채택 — 전부 실패 시에만 en fallback.
 */
export function detectBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }

  // #599 codex R1: navigator.languages 가 사용자 설정 우선순위 배열(W3C)이므로
  // 존재 시 그 순서를 최우선으로 존중하고, navigator.language 는 구형 브라우저
  // 폴백 후보로 뒤에 둔다 (중복은 Set 이 제거).
  const candidates = new Set<string>([
    ...(navigator.languages ?? []),
    ...(navigator.language ? [navigator.language] : []),
  ]);

  for (const candidate of candidates) {
    const matched = normalizeLanguageTag(candidate);
    if (matched) {
      return matched;
    }
  }

  // 매칭 실패 시 API fallback 체인과 동일하게 영어 우선
  return BROWSER_LOCALE_FALLBACK;
}

/** 로케일 정보 조회 */
export function getLocaleInfo(code: Locale): LocaleInfo | undefined {
  return SUPPORTED_LOCALES.find((l) => l.code === code);
}

/** RTL 로케일 여부 */
export function isRtlLocale(code: Locale): boolean {
  return getLocaleInfo(code)?.direction === 'rtl';
}

/** localStorage 키 */
export const LOCALE_STORAGE_KEY = 'olulo-locale';

/**
 * Language 타입 (기존 UI 번역 호환)
 *
 * 기존 코드에서 사용하던 3개 언어 타입입니다.
 * 새 코드는 Locale 타입을 사용하세요.
 * @deprecated Locale 타입을 사용하세요
 */
export type Language = 'ko' | 'es' | 'en';
